import React from 'react';
import { TableHead, TableRow, TableCell, Box } from '@mui/material';
import { DataTableColumn, DataTableSelection } from '../tableTypes';
import WcnSortIcon, { SortDirection } from '../../shared/WcnSortIcon.tsx';

function getJustifyContent(align?: string): string {
    if (align === 'center') return 'center';
    if (align === 'right') return 'flex-end';
    return 'flex-start';
}

interface WcnTableHeadProps<T> {
    readonly columns: DataTableColumn<T>[];
    readonly hasExpandableRows: boolean;
    readonly selection?: DataTableSelection<T>;
    readonly activeSortBy: string;
    readonly activeSortDirection: 'asc' | 'desc';
    readonly onSortClick: (columnValue: string, direction: 'asc' | 'desc') => void;
}

function WcnTableHead<T>({
    columns,
    hasExpandableRows,
    selection,
    activeSortBy,
    activeSortDirection,
    onSortClick
}: Readonly<WcnTableHeadProps<T>>) {
    const hasSortableColumns = columns.some((col) => col.sortable !== false);

    return (
        <TableHead>
            <TableRow sx={{ willChange: 'transform' }}>
                {hasExpandableRows && <TableCell width={40} />}
                {selection && <TableCell className="table-checkbox-cell" />}
                {columns.map((column) => {
                    const isSortable = column.sortable !== false && hasSortableColumns;
                    const activeDirection: SortDirection | null =
                        activeSortBy === column.value ? activeSortDirection : null;
                    const handleChange = (dir: SortDirection | null) => {
                        if (dir === null) {
                            onSortClick(column.value, activeSortDirection);
                        } else {
                            onSortClick(column.value, dir);
                        }
                    };
                    return (
                        <TableCell
                            key={column.value}
                            width={column.width}
                            align={column.align}
                            sx={{ cursor: 'default', userSelect: 'none', whiteSpace: 'normal', verticalAlign: 'middle' }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    justifyContent: getJustifyContent(column.align)
                                }}
                            >
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        whiteSpace: 'normal',
                                        overflowWrap: 'break-word',
                                        textAlign: column.align ?? 'left'
                                    }}
                                >
                                    {column.text || null}
                                </Box>
                                {isSortable && (
                                    <Box sx={{ flexShrink: 0, display: 'flex' }}>
                                        <WcnSortIcon
                                            activeDirection={activeDirection}
                                            compact={!column.text}
                                            onChange={handleChange}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
}

WcnTableHead.displayName = 'WcnTableHead';

export default React.memo(WcnTableHead) as typeof WcnTableHead;
